import React, { useRef } from 'react';

/**
 * Modal/backdrop wrapper that only closes when the user both PRESSES and RELEASES
 * directly on the overlay itself — not when a drag started inside the modal
 * (e.g. text selection) happens to end on the overlay.
 *
 * Why this exists: a plain `<div onClick={onClose}>` overlay fires `click` on
 * itself whenever mouseup happens on it, even if mousedown was on a child
 * element. That dismisses the modal mid-text-selection, dropping the user's
 * unsaved work. By tracking mousedown target via ref, we close only when the
 * interaction stayed on the overlay backdrop the whole time.
 *
 * Drop-in replacement for `<div className={styles.overlay} onClick={onClose}>`:
 *
 *   <Overlay className={styles.overlay} onClose={() => setEditing(null)}>
 *     <div className={styles.modal}>... form ...</div>
 *   </Overlay>
 *
 * Any other props (style, role, aria-*, ...) pass through to the wrapping div.
 */
export default function Overlay({ onClose, className, children, ...rest }) {
  const mouseDownOnBackdrop = useRef(false);

  const handleMouseDown = (e) => {
    mouseDownOnBackdrop.current = e.target === e.currentTarget;
  };

  const handleClick = (e) => {
    if (mouseDownOnBackdrop.current && e.target === e.currentTarget && onClose) {
      onClose();
    }
    mouseDownOnBackdrop.current = false;
  };

  return (
    <div
      className={className}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </div>
  );
}
